(() => {
  const root = document.documentElement;
  const body = document.body;
  const money = (cents) => {
    const format = window.Funkar?.moneyFormat || 'Rs {{amount}}';
    const value = (Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  const lock = () => body.classList.add('is-locked');
  const unlock = () => { if (!document.querySelector('.is-open')) body.classList.remove('is-locked'); };
  const drawerOpeners = new WeakMap();
  const focusableIn = (element) => [...element.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((item) => item.offsetParent !== null && item.tabIndex >= 0);
  const submitSearch = (form) => {
    if (!form) return;
    const query = String(new FormData(form).get('q') || '').trim();
    const url = new URL(form.action, location.origin);
    url.searchParams.set('q', query);
    location.assign(url.toString());
  };
  const openDrawer = (drawer, opener = document.activeElement) => {
    if (!drawer) return;
    document.querySelectorAll('.mobile-drawer.is-open,.search-drawer.is-open,.cart-drawer.is-open').forEach((open) => { if (open !== drawer) closeDrawer(open, false); });
    if (opener instanceof HTMLElement) drawerOpeners.set(drawer, opener);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lock();
    (drawer.querySelector('[data-drawer-initial-focus]') || drawer.querySelector('button, input, a'))?.focus();
  };
  const closeDrawer = (drawer, restoreFocus = true) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const opener = drawerOpeners.get(drawer);
    if (restoreFocus && opener?.isConnected) opener.focus();
    setTimeout(unlock, 20);
  };
  const loadHeroSlide = (slide) => {
    if (!slide || slide.dataset.heroLoaded === 'true') return;
    slide.querySelectorAll('source[data-srcset]').forEach((source) => {
      source.srcset = source.dataset.srcset;
      source.removeAttribute('data-srcset');
    });
    slide.querySelectorAll('img[data-src]').forEach((image) => {
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
    });
    slide.dataset.heroLoaded = 'true';
  };
  const showHeroSlide = (hero, requestedIndex) => {
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    if (!slides.length) return;
    const index = (Number(requestedIndex) + slides.length) % slides.length;
    loadHeroSlide(slides[index]);
    loadHeroSlide(slides[(index + 1) % slides.length]);
    slides.forEach((slide, position) => {
      const active = position === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    dots.forEach((dot, position) => {
      const active = position === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-pressed', String(active));
    });
    hero.dataset.heroIndex = String(index);
  };

  /* -- wishlist: persisted to localStorage as an array of product handles -- */
  const WISHLIST_KEY = 'funkar:wishlist';
  const readWishlist = () => {
    try {
      const items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(items) ? items.filter((item) => typeof item === 'string') : [];
    } catch (e) { return []; }
  };
  const writeWishlist = (items) => {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(items)); } catch (e) { /* storage unavailable */ }
  };
  const wishlistHandleFor = (button) => {
    const explicit = button.getAttribute('data-wishlist-toggle') || button.getAttribute('data-product-handle') || '';
    if (explicit && explicit !== 'true') return explicit;
    const match = location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };
  const renderWishlistButton = (button, saved) => {
    button.setAttribute('aria-pressed', String(saved));
    button.innerHTML = `<span aria-hidden="true">${saved ? '♥' : '♡'}</span> ${saved ? 'Saved' : 'Save to wishlist'}`;
  };
  const restoreWishlist = () => {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    if (!buttons.length) return;
    const items = readWishlist();
    buttons.forEach((button) => renderWishlistButton(button, items.includes(wishlistHandleFor(button))));
  };

  const initPredictiveSearch = () => {
    const form = document.querySelector('[data-predictive-search-form]');
    const input = form?.querySelector('[data-predictive-search-input]');
    const results = document.querySelector('[data-predictive-search]');
    if (!form || !input || !results) return;
    let timer = 0;
    let controller = null;
    const clear = () => {
      controller?.abort();
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };
    const load = async () => {
      const query = input.value.trim();
      if (query.length < 2) { clear(); return; }
      controller?.abort();
      controller = new AbortController();
      const rootUrl = window.Shopify?.routes?.root || '/';
      const url = `${rootUrl}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Predictive search unavailable');
        const text = await response.text();
        const documentResponse = new DOMParser().parseFromString(text, 'text/html');
        const section = documentResponse.querySelector('#shopify-section-predictive-search');
        results.innerHTML = section?.innerHTML || '';
        results.hidden = !results.textContent.trim();
        input.setAttribute('aria-expanded', String(!results.hidden));
      } catch (error) {
        if (error.name !== 'AbortError') clear();
      }
    };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(load, 180);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        const first = results.querySelector('[data-predictive-item]');
        if (first) { event.preventDefault(); first.focus(); }
      }
      if (event.key === 'Escape') clear();
    });
  };

  const initDeliveryEstimate = () => {
    document.querySelectorAll('[data-delivery-estimate]').forEach((element) => {
      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        let remaining = Number(days);
        while (remaining > 0) {
          result.setDate(result.getDate() + 1);
          if (result.getDay() !== 0 && result.getDay() !== 6) remaining -= 1;
        }
        return result;
      };
      const start = addBusinessDays(new Date(), Number(element.dataset.minDays || 5));
      const end = addBusinessDays(new Date(), Number(element.dataset.maxDays || 9));
      const locale = document.documentElement.lang || 'en-PK';
      const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
      element.textContent = `${element.dataset.prefix || 'Arrives'} ${formatter.format(start)}–${formatter.format(end)}`;
    });
  };

  const initRecentlyViewed = () => {
    const currentNode = document.querySelector('[data-recent-product]');
    const section = document.querySelector('[data-recently-viewed]');
    const grid = section?.querySelector('[data-recently-viewed-grid]');
    if (!currentNode || !section || !grid) return;
    const key = 'funkar:recent-products';
    try {
      const current = JSON.parse(currentNode.textContent);
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      const products = Array.isArray(stored) ? stored.filter((item) => item?.handle && item.handle !== current.handle).slice(0, 4) : [];
      grid.innerHTML = products.map((item) => `<article class="product-card"><a class="product-card__media" href="${escapeHtml(item.url)}" aria-label="${escapeHtml(item.title)}">${item.image ? `<img class="product-card__image product-card__image--primary" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || item.title)}" width="600" height="900" loading="lazy">` : ''}</a><div class="product-card__meta"><a class="product-card__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a><div class="price">${escapeHtml(item.price)}</div></div></article>`).join('');
      section.hidden = products.length === 0;
      localStorage.setItem(key, JSON.stringify([current, ...products].slice(0, 8)));
    } catch (error) { section.hidden = true; }
  };

  document.addEventListener('click', (event) => {
    const heroDot = event.target.closest('[data-hero-dot]');
    if (heroDot) {
      const hero = heroDot.closest('[data-hero-slider]');
      if (hero) showHeroSlide(hero, heroDot.dataset.heroDot);
      return;
    }
    const searchSubmit = event.target.closest('.search-submit');
    if (searchSubmit) { event.preventDefault(); submitSearch(searchSubmit.form); return; }
    const gridButton = event.target.closest('[data-grid-size]');
    if (gridButton) {
      const grid = document.querySelector('[data-collection-grid]');
      document.querySelectorAll('[data-grid-size]').forEach(button => button.setAttribute('aria-pressed', String(button === gridButton)));
      grid?.classList.toggle('product-grid--three', gridButton.dataset.gridSize === 'three');
      return;
    }
    const mediaButton = event.target.closest('[data-media-target]');
    if (mediaButton) {
      const target = document.getElementById(mediaButton.dataset.mediaTarget);
      const track = target?.parentElement;
      if (target && track) {
        const trackRect = track.getBoundingClientRect();
        const rect = target.getBoundingClientRect();
        track.scrollTo({
          left: track.scrollLeft + (rect.left - trackRect.left),
          top: track.scrollTop + (rect.top - trackRect.top),
          behavior: 'auto'
        });
      }
      mediaButton.parentElement.querySelectorAll('[data-media-target]').forEach(button => button.classList.toggle('is-active', button === mediaButton));
      return;
    }
    const wishlistButton = event.target.closest('[data-wishlist-toggle]');
    if (wishlistButton) {
      const saved = wishlistButton.getAttribute('aria-pressed') !== 'true';
      renderWishlistButton(wishlistButton, saved);
      const handle = wishlistHandleFor(wishlistButton);
      if (handle) {
        const items = readWishlist();
        const index = items.indexOf(handle);
        if (saved && index === -1) items.push(handle);
        if (!saved && index > -1) items.splice(index, 1);
        writeWishlist(items);
      }
      return;
    }
    const trendingTab = event.target.closest('[data-trending-tab]');
    if (trendingTab) {
      const section = trendingTab.closest('.trending');
      section.querySelectorAll('[data-trending-tab]').forEach(tab => tab.setAttribute('aria-selected', String(tab === trendingTab)));
      section.querySelectorAll('[data-trending-panel]').forEach(panel => panel.hidden = panel.dataset.trendingPanel !== trendingTab.dataset.trendingTab);
      return;
    }
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) { event.preventDefault(); openDrawer(document.querySelector(opener.dataset.drawerOpen), opener); return; }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) { event.preventDefault(); closeDrawer(closer.closest('.mobile-drawer,.search-drawer,.cart-drawer')); return; }
    const qty = event.target.closest('[data-qty-button]');
    if (qty) {
      const input = qty.parentElement.querySelector('input');
      input.value = Math.max(1, Number(input.value || 1) + Number(qty.dataset.qtyButton));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches('#FunkarSearch input[type="search"]')) {
      event.preventDefault();
      submitSearch(event.target.form);
      return;
    }
    const openDrawerElement = document.querySelector('.mobile-drawer.is-open,.search-drawer.is-open,.cart-drawer.is-open');
    if (event.key === 'Escape') document.querySelectorAll('.is-open').forEach((drawer) => closeDrawer(drawer));
    if (event.key === 'Tab' && openDrawerElement) {
      const focusable = focusableIn(openDrawerElement);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  const updateCartCount = async () => {
    const cart = await fetch('/cart.js').then(r => r.json());
    document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
  };
  const refreshCartDrawer = async (open = true) => {
    const html = await fetch('/?section_id=fk-cart-drawer').then(r => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('#FunkarCartDrawer');
    const current = document.querySelector('#FunkarCartDrawer');
    if (incoming && current) current.replaceWith(incoming);
    await updateCartCount();
    if (open) openDrawer(document.querySelector('#FunkarCartDrawer'));
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-product-status]');
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error((await response.json()).description || 'Unable to add this item.');
      if (status) status.textContent = '';
      await refreshCartDrawer(true);
    } catch (error) { if (status) status.textContent = error.message; }
    finally {
      button.disabled = false;
      button.textContent = button.dataset.label || 'Add to bag';
    }
  });

  /* -- collection toolbar: keep sort and filters from clobbering each other -- */
  const submitSortForm = (form) => {
    if (!form) return;
    const skip = new Set(['page']);
    const existing = new Set([...form.elements].map((el) => el.name).filter(Boolean));
    new URLSearchParams(location.search).forEach((value, key) => {
      if (skip.has(key) || existing.has(key) || value === '') return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
    form.submit();
  };

  document.addEventListener('submit', (event) => {
    const filterForm = event.target.closest('.filter-form');
    if (!filterForm) return;
    const sortBy = new URLSearchParams(location.search).get('sort_by');
    if (sortBy && !filterForm.querySelector('[name="sort_by"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'sort_by';
      input.value = sortBy;
      filterForm.appendChild(input);
    }
  });

  const updateFilterCount = () => {
    const summary = document.querySelector('.collection-filter-master > summary');
    if (!summary) return;
    let count = 0;
    new URLSearchParams(location.search).forEach((value, key) => {
      if (key.startsWith('filter.') && value !== '') count += 1;
    });
    let badge = summary.querySelector('.filter-count');
    if (!count) { if (badge) badge.remove(); return; }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'filter-count';
      summary.appendChild(badge);
    }
    badge.textContent = String(count);
  };

  document.addEventListener('change', async (event) => {
    if (event.target.matches('[data-auto-submit]')) { submitSortForm(event.target.form); return; }
    if (event.target.matches('[data-cart-line-quantity]')) {
      const line = event.target.dataset.cartLineQuantity;
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity: Number(event.target.value) }) });
      await refreshCartDrawer(true);
    }
    const form = event.target.closest('[data-product-form]');
    if (form && event.target.matches('[data-option-value]')) {
      const options = [...form.querySelectorAll('[data-option-group]')].map(group => group.querySelector('[data-option-value]:checked')?.value);
      const variants = JSON.parse(form.querySelector('[data-product-json]').textContent);
      const variant = variants.find(v => v.options.every((value, i) => value === options[i]));
      const id = form.querySelector('[name="id"]');
      const add = form.querySelector('[type="submit"]');
      const price = document.querySelector('[data-product-price]');
      const stock = document.querySelector('[data-stock-badge]');
      const restock = document.querySelector('[data-restock-panel]');
      if (variant) {
        id.value = variant.id; add.disabled = !variant.available; add.textContent = variant.available ? add.dataset.label : 'Sold out';
        if (price) price.textContent = money(variant.price);
        if (stock) { stock.textContent = variant.available ? 'In Stock' : 'Sold Out'; stock.classList.toggle('is-sold-out', !variant.available); }
        if (restock) {
          restock.hidden = variant.available;
          const template = restock.dataset.messageTemplate || '';
          const bodyField = restock.querySelector('[data-restock-body]');
          if (bodyField) bodyField.value = template.replace('__PRODUCT__', restock.dataset.productTitle || '').replace('__VARIANT__', variant.title || '');
        }
        history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      }
    }
  });

  /* -- gallery: swiping or scrolling the track updates the active thumb -- */
  const syncGalleryThumbs = () => {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('.product-gallery__track');
      const buttons = [...gallery.querySelectorAll('[data-media-target]')];
      if (!track || !buttons.length) return;
      let raf = 0;
      const update = () => {
        raf = 0;
        const trackRect = track.getBoundingClientRect();
        let best = null;
        let bestDistance = Infinity;
        buttons.forEach((button) => {
          const item = document.getElementById(button.dataset.mediaTarget);
          if (!item) return;
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.left - trackRect.left) + Math.abs(rect.top - trackRect.top);
          if (distance < bestDistance) { bestDistance = distance; best = button; }
        });
        if (best) buttons.forEach((button) => button.classList.toggle('is-active', button === best));
      };
      track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    });
  };

  const initProductZoom = () => {
    document.querySelectorAll('[data-product-zoom-dialog]').forEach((dialog) => {
      const image = dialog.querySelector('[data-product-zoom-image]');
      const viewport = dialog.querySelector('[data-product-zoom-viewport]');
      const level = dialog.querySelector('[data-product-zoom-level]');
      const counter = dialog.querySelector('[data-product-zoom-counter]');
      const thumbs = [...dialog.querySelectorAll('[data-product-zoom-thumb]')];
      const galleryRoot = dialog.closest('.product-main__grid') || document;
      const openers = [...galleryRoot.querySelectorAll('[data-product-zoom-open]')];
      if (!image || !viewport || !openers.length) return;

      let current = 0;
      let scale = 1;
      let panX = 0;
      let panY = 0;
      let opener = null;
      let gesture = null;
      const pointers = new Map();

      const formatPosition = (value) => String(value).padStart(2, '0');
      const clampPan = () => {
        const maxX = Math.max(0, (image.clientWidth * scale - viewport.clientWidth) / 2);
        const maxY = Math.max(0, (image.clientHeight * scale - viewport.clientHeight) / 2);
        panX = Math.min(maxX, Math.max(-maxX, panX));
        panY = Math.min(maxY, Math.max(-maxY, panY));
      };

      const renderScale = () => {
        clampPan();
        image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
        dialog.classList.toggle('is-zoomed', scale > 1);
        if (level) level.textContent = `${Math.round(scale * 100)}%`;
      };
      const setScale = (nextScale) => {
        scale = Math.min(4, Math.max(1, nextScale));
        if (scale === 1) { panX = 0; panY = 0; }
        renderScale();
      };
      const resetScale = () => {
        scale = 1;
        panX = 0;
        panY = 0;
        renderScale();
      };
      const loadImage = (requestedIndex) => {
        current = (Number(requestedIndex) + openers.length) % openers.length;
        const source = openers[current];
        image.src = source.dataset.zoomSrc;
        image.alt = source.dataset.zoomAlt || '';
        resetScale();
        if (counter) {
          counter.textContent = `${formatPosition(current + 1)} / ${formatPosition(openers.length)}`;
          counter.setAttribute('aria-label', `Image ${current + 1} of ${openers.length}`);
        }
        thumbs.forEach((thumb, index) => {
          const active = index === current;
          thumb.classList.toggle('is-active', active);
          if (active) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        [current - 1, current + 1].forEach((index) => {
          const sourceToPreload = openers[(index + openers.length) % openers.length]?.dataset.zoomSrc;
          if (sourceToPreload) { const preload = new Image(); preload.src = sourceToPreload; }
        });
      };
      const showImage = (trigger) => {
        opener = trigger;
        loadImage(Number(trigger.dataset.zoomIndex || 0));
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        requestAnimationFrame(() => renderScale());
      };
      const closeDialog = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      };
      const previousImage = () => loadImage(current - 1);
      const nextImage = () => loadImage(current + 1);

      openers.forEach((trigger) => trigger.addEventListener('click', () => showImage(trigger)));
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => loadImage(Number(thumb.dataset.productZoomThumb))));
      dialog.querySelector('[data-product-zoom-in]')?.addEventListener('click', () => setScale(scale + 0.5));
      dialog.querySelector('[data-product-zoom-out]')?.addEventListener('click', () => setScale(scale - 0.5));
      dialog.querySelector('[data-product-zoom-reset]')?.addEventListener('click', resetScale);
      dialog.querySelector('[data-product-zoom-close]')?.addEventListener('click', closeDialog);
      dialog.querySelector('[data-product-zoom-previous]')?.addEventListener('click', previousImage);
      dialog.querySelector('[data-product-zoom-next]')?.addEventListener('click', nextImage);
      viewport?.addEventListener('dblclick', () => setScale(scale === 1 ? 2 : 1));
      viewport.addEventListener('wheel', (event) => {
        event.preventDefault();
        setScale(scale + (event.deltaY < 0 ? 0.25 : -0.25));
      }, { passive: false });
      viewport.addEventListener('pointerdown', (event) => {
        viewport.setPointerCapture?.(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        viewport.classList.add('is-interacting');
        if (pointers.size === 1) {
          gesture = { mode: 'drag', x: event.clientX, y: event.clientY, panX, panY, started: performance.now() };
        } else if (pointers.size === 2) {
          const [first, second] = [...pointers.values()];
          gesture = { mode: 'pinch', distance: Math.hypot(second.x - first.x, second.y - first.y), scale };
        }
      });
      viewport.addEventListener('pointermove', (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 2 && gesture?.mode === 'pinch') {
          const [first, second] = [...pointers.values()];
          const distance = Math.hypot(second.x - first.x, second.y - first.y);
          setScale(gesture.scale * (distance / Math.max(1, gesture.distance)));
          return;
        }
        if (pointers.size === 1 && scale > 1 && gesture?.mode === 'drag') {
          panX = gesture.panX + event.clientX - gesture.x;
          panY = gesture.panY + event.clientY - gesture.y;
          renderScale();
        }
      });
      const finishPointer = (event) => {
        const completed = gesture;
        pointers.delete(event.pointerId);
        if (!pointers.size) {
          viewport.classList.remove('is-interacting');
          if (scale === 1 && completed?.mode === 'drag' && performance.now() - completed.started < 900) {
            const travelled = event.clientX - completed.x;
            if (Math.abs(travelled) > 70) travelled < 0 ? nextImage() : previousImage();
          }
          gesture = null;
          renderScale();
          return;
        }
        if (pointers.size === 1) {
          const remaining = [...pointers.values()][0];
          gesture = { mode: 'drag', x: remaining.x, y: remaining.y, panX, panY, started: performance.now() };
        }
      };
      viewport.addEventListener('pointerup', finishPointer);
      viewport.addEventListener('pointercancel', finishPointer);
      image.addEventListener('load', renderScale);
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); previousImage(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); nextImage(); }
        if (event.key === '+' || event.key === '=') { event.preventDefault(); setScale(scale + 0.5); }
        if (event.key === '-') { event.preventDefault(); setScale(scale - 0.5); }
        if (event.key === '0') { event.preventDefault(); resetScale(); }
      });
      dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
      dialog.addEventListener('close', () => {
        image.removeAttribute('src');
        image.alt = '';
        pointers.clear();
        gesture = null;
        resetScale();
        if (opener?.isConnected) opener.focus();
      });
    });
  };

  /* -- newsletter: submit feedback and a success line after redirect -- */
  const initNewsletter = () => {
    const forms = [...document.querySelectorAll('form.newsletter-form')];
    if (!forms.length) return;
    const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';
    forms.forEach((form) => {
      form.addEventListener('submit', () => {
        const button = form.querySelector('[type="submit"]');
        if (!button) return;
        if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = 'Subscribing...';
      });
      if (posted && !form.querySelector('.form-message')) {
        const message = document.createElement('p');
        message.className = 'form-message';
        message.textContent = 'Subscribed. Welcome to Funkar PK.';
        form.appendChild(message);
      }
    });
    window.addEventListener('pageshow', () => {
      forms.forEach((form) => {
        const button = form.querySelector('[type="submit"]');
        if (button && button.dataset.originalLabel) {
          button.disabled = false;
          button.textContent = button.dataset.originalLabel;
        }
      });
    });
  };

  document.querySelectorAll('[data-hero-slider]').forEach(hero => {
    let timer;
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    loadHeroSlide(slides[0]);
    loadHeroSlide(slides[1]);
    const play = () => {
      clearInterval(timer);
      timer = setInterval(() => showHeroSlide(hero, Number(hero.dataset.heroIndex || 0) + 1), 7000);
    };
    const pause = () => clearInterval(timer);
    hero.dataset.heroIndex = '0';
    hero.addEventListener('mouseenter', pause);
    hero.addEventListener('mouseleave', play);
    hero.addEventListener('focusin', pause);
    hero.addEventListener('focusout', play);
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) play();
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }), { rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('[data-reveal]').forEach(el => observer.observe(el));
  } else document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));

  const ready = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };
  ready(() => {
    restoreWishlist();
    initPredictiveSearch();
    initDeliveryEstimate();
    initRecentlyViewed();
    syncGalleryThumbs();
    initProductZoom();
    initNewsletter();
    updateFilterCount();
  });
})();

/* ============ FUNKAR motion + speed layer ============ */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  root.classList.add('fk-motion');
  if (document.startViewTransition) root.classList.add('fk-vt');

  /* -- scroll reveals: tag content inside each section, stagger siblings -- */
  const REVEAL = '.product-card, .category-panel, .colour-card, .trending article, .section-heading, .craft__copy, .mommy__copy, .intro__copy, .newsletter__inner, .duo__panel, h2, .fk-lede';
  const MEDIA = '.craft__media, .mommy__media, .duo__panel';
  if (!reduced && 'IntersectionObserver' in window) {
    const secObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('fk-in'); secObserver.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });

    document.querySelectorAll('main .shopify-section > section, main .shopify-section > div').forEach((section) => {
      if (section.closest('.hero--campaign') || section.querySelector('.hero--campaign')) return;
      let i = 0;
      section.querySelectorAll(REVEAL).forEach((el) => {
        if (el.closest('.fk-r')) return;
        el.classList.add('fk-r');
        if (el.matches(MEDIA)) el.classList.add('fk-r--media');
        el.style.setProperty('--fk-d', String(Math.min(i, 6)));
        i += 1;
      });
      if (i) { section.classList.add('fk-sec'); secObserver.observe(section); }
    });

    /* fail-safe: if the observer misses (old browsers, edge states), reveal
       anything on screen on scroll, and reveal everything after 6s idle-below-fold */
    const revealInView = () => document.querySelectorAll('.fk-sec:not(.fk-in)').forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.94 && rect.bottom > 0) section.classList.add('fk-in');
    });
    window.addEventListener('scroll', revealInView, { passive: true });
    window.addEventListener('load', revealInView);
    setTimeout(revealInView, 800);
  }

  /* -- header: shadow when scrolled, hide on scroll down, show on scroll up -- */
  let lastY = window.scrollY;
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      document.body.classList.toggle('fk-scrolled', y > 24);
      if (Math.abs(y - lastY) > 8) {
        document.body.classList.toggle('fk-scroll-down', y > lastY && y > 320);
        lastY = y;
      }
      ticking = false;
    });
  }, { passive: true });

  /* -- leave fade for browsers without cross-document view transitions -- */
  if (!document.startViewTransition && !reduced) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname === location.pathname) return;
      if (url.pathname.startsWith('/cart') || url.pathname.startsWith('/checkout')) return;
      event.preventDefault();
      document.body.classList.add('fk-leaving');
      setTimeout(() => { location.href = url.href; }, 200);
    });
    window.addEventListener('pageshow', () => document.body.classList.remove('fk-leaving'));
  }

  /* -- speed: prefetch next pages the moment intent shows -- */
  const prefetched = new Set();
  const prefetch = (href) => {
    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin || prefetched.has(url.pathname)) return;
      if (!/^\/(products|collections|pages|blogs)\//.test(url.pathname) && url.pathname !== '/') return;
      prefetched.add(url.pathname);
      const tag = document.createElement('link');
      tag.rel = 'prefetch';
      tag.href = url.pathname + url.search;
      document.head.appendChild(tag);
    } catch (e) { /* ignore malformed URLs */ }
  };
  ['pointerover', 'touchstart'].forEach((type) => document.addEventListener(type, (event) => {
    const link = event.target.closest('a[href]');
    if (link) prefetch(link.href);
  }, { passive: true, capture: true }));
})();
